import type {
  DeletableEntity,
  DeleteOutcome,
  DeletePlan,
  DeleteReference,
  RecordDeletionGateway,
} from '@forq/contracts';

/**
 * Ein Datensatz, wie ihn der Demo-Modus zum Löschen kennt.
 * Die Verweise kommen hier von den Modulen, in Supabase aus den Fremdschlüsseln.
 */
export interface DemoDeletableRecord {
  readonly entity: DeletableEntity;
  readonly id: string;
  readonly label: string;
  readonly references: readonly DeleteReference[];
  /** Gesetzt, wenn der Datensatz gar nicht weg darf (abgerechneter Vorgang). */
  readonly blockedReason?: string | null;
  archived?: boolean;
}

/**
 * Löschen im Demo-Modus -- nach DENSELBEN Regeln wie die Datenbank:
 * nichts dran = löschen, etwas dran = archivieren, gebuchter Beleg = gar nicht.
 *
 * Bewusst geteilt statt je Modul nachgebaut: liefen Demo und Datenbank auseinander, würde die
 * Vorschau etwas anderes versprechen als der Ernstfall tut -- und geübt wird im Demo-Modus.
 */
export interface DemoRecordDeletionOptions {
  readonly records?: readonly DemoDeletableRecord[];
  /**
   * Wird nach einem echten Löschen aufgerufen, damit der Datensatz auch aus dem Modul-Gateway
   * verschwindet. Ohne das meldet der Demo-Modus "gelöscht", während der Eintrag stehen bleibt --
   * und eine Demo, die etwas anderes zeigt als der Ernstfall tut, ist schlimmer als keine.
   */
  readonly onDeleted?: (entity: DeletableEntity, id: string) => void;
  /** Gegenstück fürs Archivieren: der Eintrag verschwindet ebenfalls aus den Listen. */
  readonly onArchived?: (entity: DeletableEntity, id: string) => void;
}

export class DemoRecordDeletionGateway implements RecordDeletionGateway {
  private readonly records = new Map<string, DemoDeletableRecord>();
  private readonly options: DemoRecordDeletionOptions;

  constructor(options: DemoRecordDeletionOptions | readonly DemoDeletableRecord[] = {}) {
    this.options = Array.isArray(options) ? { records: options } : (options as DemoRecordDeletionOptions);
    (this.options.records ?? []).forEach((record) => this.records.set(key(record.entity, record.id), { ...record }));
  }

  /** Module melden ihre Datensätze an, damit die Vorschau echte Verweise zeigt. */
  register(record: DemoDeletableRecord): void {
    this.records.set(key(record.entity, record.id), { ...record });
  }

  async planDeletion(_tenantId: string, entity: DeletableEntity, id: string): Promise<DeletePlan> {
    const record = this.records.get(key(entity, id));
    const references = record?.references ?? [];
    const referenceCount = references.reduce((sum, entry) => sum + entry.count, 0);
    const blockedReason = record?.blockedReason ?? null;

    return {
      entity,
      id,
      label: record?.label ?? '',
      archived: Boolean(record?.archived),
      references,
      referenceCount,
      blockedReason,
      verdict: blockedReason ? 'blocked' : referenceCount > 0 ? 'archive' : 'delete',
    };
  }

  async deleteRecord(tenantId: string, entity: DeletableEntity, id: string): Promise<DeleteOutcome> {
    const plan = await this.planDeletion(tenantId, entity, id);
    if (plan.verdict === 'blocked') {
      throw new Error(plan.blockedReason ?? 'Dieser Datensatz darf nicht gelöscht werden.');
    }
    if (plan.verdict === 'delete') {
      this.records.delete(key(entity, id));
      this.options.onDeleted?.(entity, id);
      return { action: 'deleted', label: plan.label };
    }
    const record = this.records.get(key(entity, id));
    if (record) record.archived = true;
    this.options.onArchived?.(entity, id);
    return { action: 'archived', label: plan.label, references: plan.references };
  }

  async restoreRecord(_tenantId: string, entity: DeletableEntity, id: string): Promise<void> {
    const record = this.records.get(key(entity, id));
    if (record) record.archived = false;
  }

  /** Nur für Tests und die Demo-Listen: ist der Datensatz archiviert? */
  isArchived(entity: DeletableEntity, id: string): boolean {
    return Boolean(this.records.get(key(entity, id))?.archived);
  }
}

function key(entity: DeletableEntity, id: string): string {
  return `${entity}:${id}`;
}

export function createDemoRecordDeletionGateway(
  options: DemoRecordDeletionOptions | readonly DemoDeletableRecord[] = {},
): DemoRecordDeletionGateway {
  return new DemoRecordDeletionGateway(options);
}
